import React, { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  type MarketingRouter,
  evaluateMarketingRouter,
} from '@luxuryescapes/contract-svc-promo/marketing-router/client';
import { getRegionCodes } from '@luxuryescapes/lib-regions';

import DebugModal from '~/components/DebugModal/DebugModal';

import { useMarketingRouterRules, useMarketingRouters } from '~/hooks/apis/promo/useMarketingRouterRules';

import type { MarketingRouterInternalQuery, MarketingRouterQuery } from '~/services/PromoService';

import { isoToSydneyDate } from '~/utils/dateFormatter';

import MarketingRouterRuleTableRow, { MarketingRouterRuleTableHead } from './MarketingRouterRuleTableRow';
import {
  MARKETING_ROUTER_UTM_FIELDS,
  type MarketingRouterUrlParseResult,
  type ParsedMarketingRouterUrl,
  parseMarketingRouterUrl,
  toMarketingRouterMatchInput,
} from './marketingRouterUrlResolver';

const URL_PARAM = 'url';
const EVALUATED_AT_PARAM = 'at';

const EMPTY_ROUTERS: Array<MarketingRouter> = [];

function ruleResultsPath(marketingRouterId: string): string {
  return `/marketing/marketing-router-rules/${encodeURIComponent(marketingRouterId)}`;
}

function ruleEditPath(marketingRouterId: string): string {
  return `${ruleResultsPath(marketingRouterId)}/edit`;
}

function formatEvaluatedAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'now (the date could not be read)' : date.toLocaleString('en-AU');
}

// One line per parse failure, shown under the URL field.
const PARSE_ERRORS: Record<Exclude<MarketingRouterUrlParseResult['status'], 'ok'>, string> = {
  'invalid-url': 'This could not be read as a URL. Paste a full link, a host with a path, or a path such as au/hotels.',
  'missing-region':
    'The path must start with a region, for example /au/metasearch?utm_source=google. The region decides which rules apply.',
  'unknown-region': 'The path starts with a segment that is not a region for this brand.',
};

function ParsedUrlSummary({ parsed, evaluatedAt }: { parsed: ParsedMarketingRouterUrl; evaluatedAt: string }) {
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Host', value: parsed.host ?? <Typography color="text.secondary">not in URL</Typography> },
    { label: 'Region', value: parsed.regionCode },
    { label: 'Page path', value: <Typography sx={{ fontFamily: 'monospace' }}>{parsed.path}</Typography> },
    ...MARKETING_ROUTER_UTM_FIELDS.map((field) => ({
      label: field,
      value: parsed.utm[field] ?? <Typography color="text.secondary">—</Typography>,
    })),
    {
      label: 'Replayed assignment',
      value: parsed.assignment ? (
        <Typography sx={{ fontFamily: 'monospace' }}>
          {parsed.assignment.mrid} → {parsed.assignment.mrvid}
        </Typography>
      ) : (
        <Typography color="text.secondary">none (no mrid/mrvid in URL)</Typography>
      ),
    },
    { label: 'Evaluated at', value: evaluatedAt ? formatEvaluatedAt(evaluatedAt) : 'now' },
  ];

  return (
    <Table size="small" aria-label="What the Marketing Router sees">
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.label}>
            <TableCell sx={{ width: 200, color: 'text.secondary' }}>{row.label}</TableCell>
            <TableCell>{row.value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function MarketingRouterResolverTab({ tenant }: { tenant: App.Tenant }) {
  // The evaluator runs against the same active snapshot the customer portal loads.
  const query: MarketingRouterQuery = { brand: tenant.brand as MarketingRouterQuery['brand'], status: 'active' };
  const routersQuery = useMarketingRouters({ query });
  const rulesListQuery: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const routers = routersQuery.data ?? EMPTY_ROUTERS;

  const regionCodes = useMemo(() => getRegionCodes(tenant.brand), [tenant.brand]);

  // The resolved URL lives in the query string so a verdict can be linked to a
  // colleague. The form is a local draft; Resolve commits it.
  const [searchParams, setSearchParams] = useSearchParams();
  const committed = useMemo(
    () => ({
      url: searchParams.get(URL_PARAM) ?? '',
      evaluatedAt: searchParams.get(EVALUATED_AT_PARAM) ?? '',
    }),
    [searchParams],
  );
  const [form, setForm] = useState(committed);
  useEffect(() => setForm(committed), [committed]);

  const commit = (next: typeof committed) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of [
      [URL_PARAM, next.url.trim()],
      [EVALUATED_AT_PARAM, next.evaluatedAt],
    ] as const) {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    }
    setSearchParams(params);
  };

  const parseResult = useMemo(
    () => (committed.url ? parseMarketingRouterUrl(committed.url, regionCodes) : null),
    [committed.url, regionCodes],
  );
  const parsed = parseResult?.status === 'ok' ? parseResult.parsed : null;
  const parseError = parseResult && parseResult.status !== 'ok' ? parseResult : null;

  // Variants are weighted-random, so the result is pinned per committed URL
  // rather than recomputed on every render.
  const result = useMemo(() => {
    if (!parsed || routers.length === 0) {
      return null;
    }
    const input = toMarketingRouterMatchInput(parsed, {
      evaluatedAt: committed.evaluatedAt ? new Date(committed.evaluatedAt) : undefined,
    });
    return evaluateMarketingRouter(routers, input, { includeEvaluationsInResult: true });
  }, [parsed, routers, committed.evaluatedAt]);

  const selected = result?.evaluation.selected ?? null;
  const selectedRule = selected?.rule ?? null;
  // The row is built from the admin's rule record, the same one the Rules tab lists, so the
  // matched rule reads exactly as it does there. The query matches the Rules tab's, so the
  // list is shared from cache rather than fetched again.
  const rulesQuery = useMarketingRouterRules({ query: rulesListQuery });
  const selectedRecord = selected
    ? rulesQuery.data?.find((rule) => rule.marketingRouterId === selected.mrid && rule.id === selected.rule?.id)
    : undefined;
  const today = useMemo(() => isoToSydneyDate(new Date().toISOString()), []);
  const [isExpanded, setIsExpanded] = useState(false);
  // A `=` in the page path almost always means the `?` before the query string was dropped,
  // which silently turns every UTM into part of the path.
  const looksLikeMissingQueryMark = parsed !== null && parsed.path.includes('=') && !committed.url.includes('?');

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          component="form"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            commit(form);
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">Resolve a URL</Typography>
              <Typography color="text.secondary" variant="body2">
                Paste a customer portal link to see which rule a visitor landing on it gets. The region and page path
                are read from the URL, the UTMs from its query string, and the active rules are evaluated exactly as the
                portal evaluates them.
              </Typography>
            </Box>
            <TextField
              label="URL"
              value={form.url}
              onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
              placeholder="au/metasearch?utm_medium=cpc&utm_source=google&utm_campaign=random"
              fullWidth
              size="small"
              autoFocus
              inputProps={{ spellCheck: false }}
              error={parseError !== null}
              helperText={
                parseError
                  ? parseError.status === 'unknown-region'
                    ? `"${parseError.segment}" is not a region for ${tenant.brand}. ${PARSE_ERRORS['unknown-region']}`
                    : PARSE_ERRORS[parseError.status]
                  : 'A full link or a path with its query string. The path must start with the region, e.g. au/metasearch.'
              }
            />
            <TextField
              label="Evaluate at (optional)"
              type="datetime-local"
              value={form.evaluatedAt}
              onChange={(event) => setForm((current) => ({ ...current, evaluatedAt: event.target.value }))}
              size="small"
              sx={{ alignSelf: 'flex-start', minWidth: 280 }}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to evaluate as of now. Set a date to test a scheduled rule."
            />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button type="submit" variant="contained" disabled={!form.url.trim() || routersQuery.isLoading}>
                Resolve
              </Button>
              <Button onClick={() => commit({ url: '', evaluatedAt: '' })} disabled={!committed.url}>
                Clear
              </Button>
              {routersQuery.isFetching ? <CircularProgress size={18} /> : null}
              <Box sx={{ flex: 1 }} />
              {committed.url ? (
                <Typography variant="caption" color="text.disabled">
                  This URL is in the page address — copy it to share the verdict.
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {routersQuery.isError ? (
        <Alert severity="error">
          {routersQuery.error instanceof Error
            ? routersQuery.error.message
            : 'Failed to load the active Marketing Router configuration.'}
        </Alert>
      ) : null}

      {parsed ? (
        <>
          {looksLikeMissingQueryMark ? (
            <Alert severity="warning">
              The page path contains <code>=</code>. If those are UTM parameters, put a <code>?</code> before them, for
              example <code>au/metasearch?utm_source=google</code>.
            </Alert>
          ) : null}

          {routersQuery.isLoading ? (
            <Typography color="text.secondary">Loading active rules…</Typography>
          ) : routers.length === 0 && !routersQuery.isError ? (
            <Alert severity="info">There are no active Marketing Router rules for {tenant.brand}.</Alert>
          ) : result ? (
            selected && selectedRule ? (
              <Paper variant="outlined">
                <Box sx={{ px: 2, pt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Matched rule
                  </Typography>
                </Box>
                {selectedRecord ? (
                  <Table aria-label="Matched Marketing Router rule">
                    <MarketingRouterRuleTableHead />
                    <TableBody>
                      <MarketingRouterRuleTableRow
                        rule={selectedRecord}
                        tenant={tenant}
                        today={today}
                        isExpanded={isExpanded}
                        onToggleExpanded={() => setIsExpanded((current) => !current)}
                      />
                    </TableBody>
                  </Table>
                ) : (
                  // The rule list is still loading, or the rule sits beyond the page the list fetches.
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 600, flex: 1 }}>{selectedRule.name}</Typography>
                    <Button component={Link} to={ruleResultsPath(selected.mrid)} variant="text">
                      Results
                    </Button>
                    <Button component={Link} to={ruleEditPath(selected.mrid)} variant="text">
                      Edit
                    </Button>
                  </Stack>
                )}
              </Paper>
            ) : (
              <Alert severity="info">No active rule matches this URL.</Alert>
            )
          ) : null}

          <Paper variant="outlined">
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                What the router sees
              </Typography>
            </Box>
            <ParsedUrlSummary parsed={parsed} evaluatedAt={committed.evaluatedAt} />
          </Paper>

          {result ? (
            <DebugModal
              buttonProps={{ variant: 'text', size: 'small', sx: { alignSelf: 'flex-start' } }}
              data={result}
              title="Debug evaluation"
              type="generic"
            />
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
